/*
 * SPDX-FileCopyrightText: 2023 CERN.
 * SPDX-License-Identifier: MIT
 */

/**
 * Add here all the overridden components of your app.
 */

import { i18next } from "@translations/invenio_app_rdm/i18next";
import _get from "lodash/get";
import React, { useEffect, useState } from "react";
import { useFormikContext } from "formik";
import { useSelector } from "react-redux";
import Dropzone from "react-dropzone";
import { FilesListTable } from "@js/invenio_rdm_records/src/deposit/fields/FileUploader/FileUploaderArea";
import { AccordionField, FieldLabel, Input, TextArea, Dropdown } from "react-invenio-forms";
import { DepositFormApp, SubjectsField } from "@js/invenio_rdm_records";
import { SearchItemCreators } from "@js/invenio_app_rdm/utils";
import { Item, Label, Icon, Checkbox, Button, Card, Form, Grid } from "semantic-ui-react";
import { CompactStats } from "@js/invenio_app_rdm/components/CompactStats";
import { DisplayPartOfCommunities } from "@js/invenio_app_rdm/components/DisplayPartOfCommunities";

// [open] vis: search results list item with a thumbnail image.
//
// The stock InvenioRDM component (invenio_app_rdm's RecordsResultsListItem.js)
// never renders an image at all, even though every record's API response
// already includes real IIIF thumbnail links (result.links.thumbnails)
// generated from files.default_preview. Inlined here (rather than a
// separate imported file) because
// only specific known files (like this one) get collected/symlinked
// into the webpack build from platform/assets/js/ - arbitrary sibling
// files under this directory are not automatically picked up.
const RecordsResultsListItemWithThumbnail = ({
  result,
  accessStatusId,
  accessStatus,
  accessStatusIcon,
  createdDate,
  creators,
  descriptionStripped,
  publicationDate,
  resourceType,
  subjects,
  title,
  version,
  versions,
  allVersionsVisible,
  numOtherVersions,
}) => {
  const resourceTypeId = _get(result, "ui.resource_type.id", "");
  const [mainType] = resourceTypeId.split("-");
  const filterValue = resourceTypeId.includes("-")
    ? `resource_type:${mainType}+inner:${resourceTypeId}`
    : `resource_type:${resourceTypeId}`;
  const resourceTypeFilter = encodeURIComponent(filterValue);

  const uniqueViews = _get(result, "stats.all_versions.unique_views", 0);
  const uniqueDownloads = _get(result, "stats.all_versions.unique_downloads", 0);
  const publishingInformation = _get(result, "ui.publishing_information.journal", "");
  // "medium" (300px) rather than the initial "tiny" (80px) - the small
  // size read as barely-there next to a full search-result row. Source
  // resolution bumped to match (750, not 250) so it isn't visibly
  // upscaled/blurry at the larger display size.
  const thumbnailUrl = _get(result, "links.thumbnails.750", null);
  const viewLink = `/records/${result.id}`;

  return (
    <Item key={result.id}>
      {thumbnailUrl && (
        <Item.Image size="medium" src={thumbnailUrl} as="a" href={viewLink} alt="" />
      )}
      <Item.Content>
        <Item.Extra className="labels-actions">
          <Label horizontal size="small" className="primary theme-primary">
            {publicationDate} ({version})
          </Label>
          <Label
            horizontal
            size="small"
            className="neutral"
            as="a"
            href={`${window.location.pathname}?q=&f=${resourceTypeFilter}`}
          >
            {resourceType}
          </Label>
          <Label
            horizontal
            size="small"
            className={`access-status ${accessStatusId}`}
          >
            {accessStatusIcon && <Icon name={accessStatusIcon} />}
            {accessStatus}
          </Label>
        </Item.Extra>
        <Item.Header as="h2" className="theme-primary-text">
          <a href={viewLink}>{title}</a>
        </Item.Header>
        <Item className="creatibutors">
          <SearchItemCreators creators={creators} othersLink={viewLink} />
        </Item>
        <Item.Description className="truncate-lines-2">
          {descriptionStripped}
        </Item.Description>
        <Item.Extra>
          {subjects.map((subject) => (
            <Label key={subject.title_l10n} size="tiny">
              {subject.title_l10n}
            </Label>
          ))}
          <div className="flex justify-space-between align-items-end">
            <small>
              <DisplayPartOfCommunities communities={result.parent?.communities} />
              <p>
                {createdDate && (
                  <>
                    {i18next.t("Uploaded on {{uploadDate}}", {
                      uploadDate: createdDate,
                    })}
                  </>
                )}
                {createdDate && publishingInformation && " | "}
                {publishingInformation && (
                  <>
                    {i18next.t("Published in: {{- publishInfo }}", {
                      publishInfo: publishingInformation,
                    })}
                  </>
                )}
              </p>
              {!allVersionsVisible && versions.index > 1 && (
                <p>
                  <b>
                    {i18next.t("{{count}} more versions exist for this record", {
                      count: numOtherVersions,
                    })}
                  </b>
                </p>
              )}
            </small>
            <small>
              <CompactStats uniqueViews={uniqueViews} uniqueDownloads={uniqueDownloads} />
            </small>
          </div>
        </Item.Extra>
      </Item.Content>
    </Item>
  );
};

// The "RecordsResultsListItem.layout" overridable region is namespaced
// per search app - react-searchkit's buildUID() prefixes it with each
// app's own appName (e.g. "InvenioAppRdm.Search.RecordsResultsListItem.layout"),
// there's no unprefixed/global registration point. Every place that
// renders RecordsResultsListItem needs its own entry here, each pointing
// at its app's actual appName constant (grep `const appName = "..."` in
// invenio_app_rdm's search/, frontpage/, collectionRecordsSearch/,
// communityRecordsSearch/, user_dashboard/uploads.js).
const RECORDS_RESULTS_LIST_APP_NAMES = [
  "InvenioAppRdm.Search", // /search
  "InvenioAppRDM.RecordsList", // frontpage "Recent uploads"
  "InvenioAppRDM.CollectionsSearch",
  "InvenioCommunities.DetailsSearch",
  "InvenioAppRdm.DashboardUploads", // "My dashboard" > Uploads
];

export const overriddenComponents = Object.fromEntries(
  RECORDS_RESULTS_LIST_APP_NAMES.map((appName) => [
    `${appName}.RecordsResultsListItem.layout`,
    RecordsResultsListItemWithThumbnail,
  ])
);


// Presentation groups come from metadata_schema.json. Native Invenio controls
// retain their upload, validation, access and draft/publish behavior.
const SCHEMA_WIDGETS = { Input, TextArea, Dropdown };
const allSchemaFields = (sections) => sections.flatMap((section) => section.fields);
const applicable = (field, values) => !field.visible_when || field.visible_when.in.includes(_get(values, field.visible_when.field));

const SchemaField = ({ field, record }) => {
  const { values } = useFormikContext();
  if (!applicable(field, values)) return null;
  const Widget = SCHEMA_WIDGETS[field.ui_widget];
  return <Widget {...field.props} record={record} fieldPath={`custom_fields.${field.field}`} />;
};

const SchemaGroup = ({ sections, group, record }) => allSchemaFields(sections)
  .filter((field) => field.form_group === group)
  .map((field) => <SchemaField key={field.field} field={field} record={record} />);

const VisualizationStart = ({ record }) => <div className="ovf-form-intro">
  <h1>{record?.is_published ? "Edit your visualization" : "Share a visualization"}</h1>
</div>;

// Keep uploads and links visible while retaining the native upload controls.
const VisualizationFiles = ({ children, config, record }) => (
  <section id="files-section" className="ovf-upload-section" aria-labelledby="ovf-upload-heading"
    data-label="Visualization and data files">
    <h2 id="ovf-upload-heading">Visualization &amp; data files</h2>
    {children}
    <div className="ovf-upload-links">
      <SchemaGroup sections={config?.custom_fields?.ui || []} group="visualization" record={record} />
      <SchemaGroup sections={config?.custom_fields?.ui || []} group="files" record={record} />
    </div>
  </section>
);

const Essentials = ({ children, config, record, includesPaths = [], severityChecks }) => {
  const { values, setFieldValue } = useFormikContext();
  useEffect(() => {
    if (!_get(values, "metadata.resource_type.id")) {
      setFieldValue("metadata.resource_type", { id: "image-other" });
    }
  }, [values.metadata?.resource_type?.id]);
  const controls = React.Children.toArray(children);
  const extraNames = ["PIDField", "CopyrightsField", "ResourceTypeField"];
  const extra = controls.filter((child) => extraNames.some((name) => child.props?.id?.includes(`.${name}.`)));
  const native = config.custom_fields.ui.flatMap((section) => section.native_fields || []);
  const primary = controls.filter((child) => !extra.includes(child)).map((control) => {
    const field = native.find((item) => item.storage === control.props?.fieldPath);
    if (!field || !React.isValidElement(control.props.children)) return control;
    return React.cloneElement(control, {}, React.cloneElement(control.props.children, {
      label: field.label, helpText: field.help || undefined, placeholder: field.placeholder,
    }));
  });
  const title = primary.filter((control) => control.props?.fieldPath === "metadata.title");
  const date = primary.filter((control) => control.props?.fieldPath === "metadata.publication_date");
  const remaining = primary.filter((control) => !title.includes(control) && !date.includes(control));
  return <AccordionField id="basic-information-section" label="Metadata" active
    includesPaths={[...includesPaths, "custom_fields.ovf:chart_type", "metadata.subjects"]} severityChecks={severityChecks}>
    {title}
    <div className="ovf-metadata-top">
      <SchemaGroup sections={config.custom_fields.ui} group="essentials" record={record} />
      {date}
    </div>
    {remaining}
    <SubjectsField fieldPath="metadata.subjects" label="Keywords" labelIcon="tag"
      placeholder="Add keywords" noQueryMessage="Type a keyword"
      initialOptions={record?.ui?.subjects || []} limitToOptions={[]} />
  </AccordionField>;
};

const SimpleTitle = ({ fieldPath = "metadata.title" }) => (
  <Input fieldPath={fieldPath} label="Title" placeholder="Give your visualization a title" required />
);

// Keep the existing rich-text editor and saved description; omit the
// additional-description list and its "Add description" action.
const SimpleDescription = ({ children }) => <>
  {React.Children.toArray(children).filter((child) =>
    child.props?.fieldPath !== "metadata.additional_descriptions")}
</>;

const blankAuthor = () => ({ person_or_org: { type: "personal", given_name: "", family_name: "" } });
const SimpleAuthors = ({ fieldPath = "metadata.creators" }) => {
  const { values, errors, touched, submitCount, setFieldValue } = useFormikContext();
  const saved = _get(values, fieldPath, []);
  const authors = saved.length ? saved : [blankAuthor()];
  const update = (index, changes) => {
    const next = authors.map((author, position) => {
      if (position !== index) return author;
      const person = { ...author.person_or_org, ...changes };
      if (person.type === "personal") {
        person.name = [person.family_name, person.given_name].filter(Boolean).join(", ");
      }
      return { ...author, person_or_org: person };
    });
    setFieldValue(fieldPath, next);
  };
  const error = _get(errors, fieldPath);
  const showError = Boolean(error && (submitCount > 0 || _get(touched, fieldPath)));
  return <div className="ovf-authors">
    <h3>Authors <span aria-hidden="true">*</span></h3>
    {authors.map((author, index) => {
      const person = author.person_or_org || blankAuthor().person_or_org;
      const organization = person.type === "organizational";
      return <div className="ovf-author-row" key={index}>
        <label>Author type
          <select aria-label={`Author ${index + 1} type`} value={person.type || "personal"}
            onChange={(event) => {
              const next = [...authors];
              next[index] = { ...author, person_or_org: event.target.value === "personal"
                ? blankAuthor().person_or_org : { type: "organizational", name: "" } };
              setFieldValue(fieldPath, next);
            }}>
            <option value="personal">Person</option><option value="organizational">Organization</option>
          </select>
        </label>
        {organization ? <label>Organization name
          <input aria-label={`Author ${index + 1} organization name`} value={person.name || ""}
            onChange={(event) => update(index, { name: event.target.value })} />
        </label> : <>
          <label>First name
            <input aria-label={`Author ${index + 1} first name`} autoComplete="off" value={person.given_name || ""}
              onChange={(event) => update(index, { given_name: event.target.value })} />
          </label>
          <label>Last name
            <input aria-label={`Author ${index + 1} last name`} autoComplete="off" value={person.family_name || ""}
              onChange={(event) => update(index, { family_name: event.target.value })} />
          </label>
        </>}
        {authors.length > 1 && <Button type="button" basic size="small" aria-label={`Remove author ${index + 1}`}
          onClick={() => setFieldValue(fieldPath, authors.filter((_, position) => position !== index))}>Remove</Button>}
      </div>;
    })}
    {showError && <p role="alert" className="ovf-author-error">Check the author names. Each person needs a last name; each organization needs a name.</p>}
    <Button type="button" basic size="small" onClick={() => setFieldValue(fieldPath, [...authors, blankAuthor()])}>Add author</Button>
  </div>;
};

// Reorder only the main column of the existing form; keep the original form
// provider, feedback, save/preview/publish sidebar, permissions and extensions.
const FriendlyDepositLayout = ({ children, config, record, preselectedCommunity, files, permissions, errors, recordSerializer }) => {
  const community = React.Children.toArray(children).find((node) =>
    node.props?.id === "InvenioAppRdm.Deposit.CommunityHeader.container");
  const visit = (node) => {
    if (!React.isValidElement(node)) return node;
    if (node.props.id === "InvenioAppRdm.Deposit.CommunityHeader.container") return null;
    if (node.props.id === "InvenioAppRdm.Deposit.CardDepositStatusBox.container"
      && community?.props.children && React.isValidElement(node.props.children)) {
      const card = node.props.children;
      const content = React.Children.toArray(card.props.children);
      const actions = content[1];
      const grid = actions?.props.children;
      if (React.isValidElement(grid)) {
        content[1] = React.cloneElement(actions, {}, React.cloneElement(grid, {},
          React.Children.toArray(grid.props.children),
          <Grid.Column width={16} className="pt-0" key="community">
            <div className="ovf-draft-community">{community}</div>
          </Grid.Column>));
      }
      return React.cloneElement(node, {}, React.cloneElement(card, {}, content));
    }
    if (node.props.computer === 11) {
      const items = React.Children.toArray(node.props.children);
      const pick = (name) => items.find((item) => item.props?.id === `InvenioAppRdm.Deposit.${name}`);
      const selected = [pick("Files.before.container"), pick("AccordionFieldFiles.container"), pick("Files.after.container"), pick("AccordionFieldBasicInformation.container"), pick("BasicInformation.after.container")].filter(Boolean);
      return React.cloneElement(node, { className: "ovf-friendly-deposit" }, selected);
    }
    if (!node.props.children) return node;
    // Overridable itself requires one element, not an array of one element.
    const content = React.isValidElement(node.props.children)
      ? visit(node.props.children)
      : React.Children.map(node.props.children, visit);
    return React.cloneElement(node, {}, content);
  };
  return <DepositFormApp config={config} record={record} preselectedCommunity={preselectedCommunity}
    files={files} permissions={permissions} errors={errors} recordSerializer={recordSerializer}>
    {React.Children.map(children, visit)}
  </DepositFormApp>;
};

// Retain the native modal trigger and permissions, with a full-width label.
const CommunityButton = (props) => {
  const community = useSelector((state) => state.deposit?.editorState?.selectedCommunity);
  return <Button {...props} fluid basic primary={false} size="medium"
    content={community?.metadata?.title || "Select a community"}
    title={community?.metadata?.title ? "Change community" : undefined} />;
};

// Use the native upload callbacks so draft creation, quotas, duplicate checks,
// cancellation and server errors follow the same lifecycle as the stock form.
const FriendlyUploadArea = (props) => {
  const { filesEnabled, filesLocked, filesList = [], dropzoneParams = {}, hasError } = props;
  const disabled = Boolean(filesLocked || dropzoneParams.disabled);
  if (!filesEnabled) return null;
  return <Grid.Row><Grid.Column width={16}>
    {!filesLocked && <Dropzone {...dropzoneParams} disabled={disabled}>
      {({ getRootProps, getInputProps, open, isDragActive, isDragReject }) => (
        <div {...getRootProps({ className: `ovf-upload-zone${isDragActive ? " is-dragging" : ""}${hasError || isDragReject ? " has-error" : ""}` })}>
          <input {...getInputProps({ "aria-label": "Upload visualization files" })} />
          <Icon name="cloud upload" size="big" aria-hidden="true" />
          <h3>{disabled ? "File limit reached" : isDragActive ? "Drop files here" : "Drag your files here"}</h3>
          <Button type="button" primary disabled={disabled} onClick={open}>
            {filesList.length ? "Add more files" : "Choose files"}
          </Button>
          {isDragReject && <p role="alert">These files cannot be added. Check the file limits and try again.</p>}
        </div>
      )}
    </Dropzone>}
    {filesList.length > 0 && <div className="ovf-upload-list">
      <FilesListTable {...props} />
    </div>}
  </Grid.Column></Grid.Row>;
};

const LinkOnlyToggle = ({ showMetadataOnlyToggle, filesList = [], filesEnabled }) => {
  const { values, setFieldValue } = useFormikContext();
  if (!showMetadataOnlyToggle) return null;
  return <div className="ovf-upload-mode">
    <Checkbox label="Share a link without files" disabled={filesList.length > 0} checked={!filesEnabled}
      onChange={() => {
        if (filesList.length) return;
        setFieldValue("files.enabled", !filesEnabled);
        setFieldValue("access.files", _get(values, "access.record", "public"));
      }} />
    {filesList.length > 0 && <small>Remove your files to switch to link only.</small>}
  </div>;
};

// Visibility is one choice, not three. Stock InvenioRDM splits it into a
// "Full record" toggle, a "Files only" toggle and an embargo, which can
// express a record whose description is public while its files are not - a
// state this platform has no way to present - and an embargo it has no use
// for. Here one switch moves access.record and access.files together and no
// embargo is ever written. OVFAccessComponent
// (site/open_vis_framework/service_components.py) enforces the same rule on
// the API, so nothing can store a state this control cannot show.
const VISIBILITY_OPTIONS = [
  {
    value: "public",
    label: "Public",
    icon: "lock open",
    describe: (metadataOnly) => metadataOnly
      ? "Your visualization is publicly accessible."
      : "Your visualization and its files are publicly accessible.",
  },
  {
    value: "restricted",
    label: "Private",
    icon: "lock",
    describe: () => "Only you and people you share it with can see this.",
  },
];

const Visibility = ({ fieldPath = "access", label = "Visibility", labelIcon = "shield",
  id = "visibility-section", record, permissions, recordRestrictionGracePeriod, allowRecordRestriction }) => {
  const { values, setFieldValue } = useFormikContext();
  const community = useSelector((state) => state.deposit?.editorState?.selectedCommunity);
  // A ghost community is one this user cannot see; stock treats it as public.
  const communityPublic = !community || community.is_ghost === true
    || community.access?.visibility === "public";
  const metadataOnly = _get(values, "files.enabled") === false;
  const selected = communityPublic && _get(values, `${fieldPath}.record`) === "public"
    ? "public" : "restricted";

  // Same rule as the stock MetadataAccess.canRestrictRecord(): where the
  // instance forbids late restriction, a published record that is past its
  // grace period stays public.
  const gracePeriodOver = Boolean(record?.created)
    && new Date(record.created).getTime() + recordRestrictionGracePeriod * 86400000 < Date.now();
  const canGoPrivate = !(!allowRecordRestriction && record?.created && record?.is_published
    && record?.access?.record !== "restricted" && gracePeriodOver);
  const canManage = permissions?.can_manage_record_access !== false;

  const choose = (visibility) => {
    setFieldValue(`${fieldPath}.record`, visibility);
    setFieldValue(`${fieldPath}.files`, visibility);
    // The form never offers an embargo, so never leave one behind either.
    setFieldValue(`${fieldPath}.embargo`, { active: false, until: null, reason: null });
  };

  // A restricted community cannot hold a public record. Move the form to the
  // value the server accepts rather than showing one it would reject.
  useEffect(() => {
    if (!communityPublic && _get(values, `${fieldPath}.record`) !== "restricted") {
      choose("restricted");
    }
  }, [communityPublic]);

  const readOnlyReason = !canManage
    ? "Only someone who can manage this record's access may change its visibility."
    : !communityPublic
      ? "This community is restricted, so its visualizations stay private."
      : !canGoPrivate
        ? "This visualization can no longer be made private. Contact support if you need that changed."
        : null;

  return (
    <Card id={id} className="access-right ovf-visibility">
      <Form.Field required>
        <Card.Content>
          <Card.Header>
            <FieldLabel htmlFor={fieldPath} icon={labelIcon} label={label} />
          </Card.Header>
        </Card.Content>
        <Card.Content>
          <Button.Group widths="2">
            {VISIBILITY_OPTIONS.map((option) => (
              <Button key={option.value} type="button" data-visibility={option.value}
                active={selected === option.value}
                className={selected === option.value ? "primary theme-primary" : ""}
                disabled={!canManage
                  || (option.value === "public" && !communityPublic)
                  || (option.value === "restricted" && !canGoPrivate)}
                onClick={() => choose(option.value)}>
                <Icon name={option.icon} /> {option.label}
              </Button>
            ))}
          </Button.Group>
          <p className="text-muted">
            {VISIBILITY_OPTIONS.find((option) => option.value === selected).describe(metadataOnly)}
          </p>
          {readOnlyReason && <p className="text-muted">{readOnlyReason}</p>}
        </Card.Content>
      </Form.Field>
    </Card>
  );
};

Object.assign(overriddenComponents, {
  "InvenioAppRdm.Deposit.RDMDepositForm.layout": FriendlyDepositLayout,
  "InvenioRdmRecords.CommunityHeader.CommunitySelectionButton.Container": CommunityButton,
  "InvenioAppRdm.Deposit.Files.before.container": VisualizationStart,
  "InvenioAppRdm.Deposit.AccordionFieldFiles.container": VisualizationFiles,
  "InvenioAppRdm.Deposit.AccordionFieldBasicInformation.container": Essentials,
  "InvenioAppRdm.Deposit.CustomFields.container": () => null,
  "InvenioAppRdm.Deposit.TitlesField.container": SimpleTitle,
  "InvenioRdmRecords.DepositForm.DescriptionsField.Container": SimpleDescription,
  "InvenioAppRdm.Deposit.CreatorsField.container": SimpleAuthors,
  "InvenioAppRdm.Deposit.AccessRightField.container": Visibility,
  "InvenioRdmRecords.DepositForm.FileUploaderToolbar.MetadataOnlyToggle": LinkOnlyToggle,
  "InvenioRdmRecords.DepositForm.FileUploader.UploadArea": FriendlyUploadArea,
});
